import { SupportedChainId, SupportedL1ChainId } from './chains'


import avalancheLogo from 'assets/svg/chain_logos_on_selection/avalanche_logo.png'
import avalancheLogoHeader from 'assets/svg/chain_logos_on_header/avalanche_logo_squared.svg'
import bscLogo from 'assets/svg/chain_logos_on_selection/bsc_logo.png'
import bscLogoHeader from 'assets/svg/chain_logos_on_header/bsc_logo_squared.svg'
import ethereumLogo from 'assets/svg/chain_logos_on_selection/ethereum_logo.svg'
import ethereumLogoHeader from 'assets/svg/chain_logos_on_header/ethereum_logo_squared.svg'
import fantomLogo from 'assets/svg/chain_logos_on_selection/fantom_logo.png'
import fantomLogoHeader from 'assets/svg/chain_logos_on_header/fantom_logo_squared.svg'

import ms from 'ms.macro'
import polygonLogo from 'assets/svg/chain_logos_on_selection/polygon_logo.png'
import polygonLogoHeader from 'assets/svg/chain_logos_on_header/polygon_logo_squared.svg'

export enum NetworkType {
  L1,
  L2,
}

interface BaseChainInfo {
  readonly networkType: NetworkType
  readonly blockWaitMsBeforeWarning?: number
  readonly docs?: string
  readonly bridge?: string
  readonly explorer: string
  readonly infoLink?: string
  readonly logoUrl: string
  readonly headerLogoUrl: string
  readonly label: string
  readonly helpCenterUrl?: string
  readonly nativeCurrency: {
    name: string // e.g. 'Goerli ETH',
    symbol: string // e.g. 'gorETH',
    decimals: number // e.g. 18,
  }
}

export interface L1ChainInfo extends BaseChainInfo {
  readonly networkType: NetworkType.L1
}

export interface L2ChainInfo extends BaseChainInfo {
  readonly networkType: NetworkType.L2
  readonly bridge: string
  readonly statusPage?: string
  readonly defaultListUrl: string
}

export type ChainInfoMap = { readonly [chainId: number]: L1ChainInfo | L2ChainInfo } & {
  readonly [chainId in SupportedL1ChainId]: L1ChainInfo
}

// ort type ChainInfoMap = { readonly [chainId: number]: L1ChainInfo | L2ChainInfo } & {
//   readonly [chainId in SupportedL2ChainId]: L2ChainInfo
// } &
//   { readonly [chainId in SupportedL1ChainId]: L1ChainInfo }

export const CHAIN_INFO: any = {
  [SupportedChainId.MAINNET]: {
    networkType: NetworkType.L1,
    docs: 'https://docs.uniswap.org/',
    explorer: 'https://etherscan.io/',
    infoLink: 'https://info.uniswap.org/#/',
    label: 'Ethereum',
    logoUrl: 'https://w7.pngwing.com/pngs/268/1013/png-transparent-ethereum-eth-hd-logo-thumbnail.png',
    to: 137,
    headerLogoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/ethereum.png',
    nativeCurrency: {
      address: '0x0000000000000000000000000000000000000000',
      chainId: 1,
      coinKey: 'ETH',
      decimals: 18,
      logoURI: 'https://static.debank.com/image/token/logo_url/eth/935ae4e4d1d12d59a99717a24f2540b5.png',
      name: 'ETH',
      symbol: 'ETH',
    },
    bridgeCurrency: [
      {
        address: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
        chainId: 1,
        coinKey: 'USDC',
        decimals: 6,
        logoURI:
          'https://static.debank.com/image/eth_token/logo_url/0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48/fffcd27b9efff5a86ab942084c05924d.png',
        name: 'USDC',
        symbol: 'USDC',
      },
      {
        address: '0xdac17f958d2ee523a2206206994597c13d831ec7',
        chainId: 1,
        coinKey: 'USDT',
        decimals: 6,
        logoURI:
          'https://static.debank.com/image/eth_token/logo_url/0xdac17f958d2ee523a2206206994597c13d831ec7/66eadee7b7bb16b75e02b570ab8d5c01.png',
        name: 'USDT',
        symbol: 'USDT',
      },
      {
        address: '0x0000000000000000000000000000000000000000',
        chainId: 1,
        coinKey: 'ETH',
        decimals: 18,
        logoURI: 'https://static.debank.com/image/token/logo_url/eth/935ae4e4d1d12d59a99717a24f2540b5.png',
        name: 'ETH',
        symbol: 'ETH',
      },
    ],
  },
  [SupportedChainId.RINKEBY]: {
    networkType: NetworkType.L1,
    docs: 'https://docs.uniswap.org/',
    explorer: 'https://rinkeby.etherscan.io/',
    infoLink: 'https://info.uniswap.org/#/',
    label: 'Rinkeby',
    logoUrl: ethereumLogo,
    headerLogoUrl: ethereumLogoHeader,
    nativeCurrency: { name: 'Rinkeby Ether', symbol: 'rETH', decimals: 18 },
  },
  [SupportedChainId.ROPSTEN]: {
    networkType: NetworkType.L1,
    docs: 'https://docs.uniswap.org/',
    explorer: 'https://ropsten.etherscan.io/',
    infoLink: 'https://info.uniswap.org/#/',
    label: 'Ropsten',
    logoUrl: ethereumLogo,
    headerLogoUrl: ethereumLogoHeader,
    nativeCurrency: { name: 'Ropsten Ether', symbol: 'ropETH', decimals: 18 },
  },
  [SupportedChainId.KOVAN]: {
    networkType: NetworkType.L1,
    docs: 'https://docs.uniswap.org/',
    explorer: 'https://kovan.etherscan.io/',
    infoLink: 'https://info.uniswap.org/#/',
    label: 'Kovan',
    logoUrl: ethereumLogo,
    headerLogoUrl: ethereumLogoHeader,
    nativeCurrency: { name: 'Kovan Ether', symbol: 'kovETH', decimals: 18 },
  },
  [SupportedChainId.GOERLI]: {
    networkType: NetworkType.L1,
    docs: 'https://docs.uniswap.org/',
    explorer: 'https://goerli.etherscan.io/',
    infoLink: 'https://info.uniswap.org/#/',
    label: 'Görli',
    logoUrl: ethereumLogo,
    headerLogoUrl: ethereumLogoHeader,
    nativeCurrency: { name: 'Görli Ether', symbol: 'görETH', decimals: 18 },
  },
  // [SupportedChainId.OPTIMISM]: {
  //   networkType: NetworkType.L2,
  //   blockWaitMsBeforeWarning: ms`25m`,
  //   bridge: 'https://gateway.optimism.io/?chainId=1',
  //   defaultListUrl: OPTIMISM_LIST,
  //   docs: 'https://optimism.io/',
  //   explorer: 'https://optimistic.etherscan.io/',
  //   infoLink: 'https://info.uniswap.org/#/optimism/',
  //   label: 'Optimism',
  //   logoUrl: optimismLogoUrl,
  //   statusPage: 'https://optimism.io/status',
  //   helpCenterUrl: 'https://help.uniswap.org/en/collections/3137778-uniswap-on-optimistic-ethereum-oξ',
  //   nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  // },
  // [SupportedChainId.OPTIMISTIC_KOVAN]: {
  //   networkType: NetworkType.L2,
  //   blockWaitMsBeforeWarning: ms`25m`,
  //   bridge: 'https://gateway.optimism.io/',
  //   defaultListUrl: OPTIMISM_LIST,
  //   docs: 'https://optimism.io/',
  //   explorer: 'https://optimistic.etherscan.io/',
  //   infoLink: 'https://info.uniswap.org/#/optimism/',
  //   label: 'Optimistic Kovan',
  //   logoUrl: optimismLogoUrl,
  //   statusPage: 'https://optimism.io/status',
  //   helpCenterUrl: 'https://help.uniswap.org/en/collections/3137778-uniswap-on-optimistic-ethereum-oξ',
  //   nativeCurrency: { name: 'Optimistic Kovan Ether', symbol: 'kovOpETH', decimals: 18 },
  // },
  // [SupportedChainId.ARBITRUM_ONE]: {
  //   networkType: NetworkType.L2,
  //   blockWaitMsBeforeWarning: ms`10m`,
  //   bridge: 'https://bridge.arbitrum.io/',
  //   docs: 'https://offchainlabs.com/',
  //   explorer: 'https://arbiscan.io/',
  //   infoLink: 'https://info.uniswap.org/#/arbitrum',
  //   label: 'Arbitrum',
  //   logoUrl: arbitrumLogoUrl,
  //   defaultListUrl: ARBITRUM_LIST,
  //   helpCenterUrl: 'https://help.uniswap.org/en/collections/3137787-uniswap-on-arbitrum',
  //   nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  // },
  // [SupportedChainId.ARBITRUM_RINKEBY]: {
  //   networkType: NetworkType.L2,
  //   blockWaitMsBeforeWarning: ms`10m`,
  //   bridge: 'https://bridge.arbitrum.io/',
  //   docs: 'https://offchainlabs.com/',
  //   explorer: 'https://rinkeby-explorer.arbitrum.io/',
  //   infoLink: 'https://info.uniswap.org/#/arbitrum/',
  //   label: 'Arbitrum Rinkeby',
  //   logoUrl: arbitrumLogoUrl,
  //   defaultListUrl: ARBITRUM_LIST,
  //   helpCenterUrl: 'https://help.uniswap.org/en/collections/3137787-uniswap-on-arbitrum',
  //   nativeCurrency: { name: 'Rinkeby Arbitrum Ether', symbol: 'rinkArbETH', decimals: 18 },
  // },
  [SupportedChainId.POLYGON]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms`10m`,
    docs: 'https://polygon.io/',
    explorer: 'https://polygonscan.com/',
    infoLink: 'https://info.uniswap.org/#/polygon/',
    label: 'Polygon',
    to: 56,
    logoUrl: 'https://seeklogo.com/images/P/polygon-matic-logo-1DFDA3A3A8-seeklogo.com.png',
    headerLogoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/polygon.png',
    nativeCurrency: {
      address: '0x0000000000000000000000000000000000000000',
      chainId: 137,
      coinKey: 'MATIC',
      decimals: 18,
      logoURI: 'https://static.debank.com/image/matic_token/logo_url/matic/e5a8a2860ba5cf740a474dcab796dc63.png',
      name: 'MATIC',

      symbol: 'MATIC',
    },
    bridgeCurrency: [
      {
        address: '0x2791bca1f2de4661ed88a30c99a7a9449aa84174',
        chainId: 137,
        coinKey: 'USDC',
        decimals: 6,
        logoURI:
        'https://static.debank.com/image/eth_token/logo_url/0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48/fffcd27b9efff5a86ab942084c05924d.png',
        name: 'USDC',

        symbol: 'USDC',
      },
      {
        address: '0xc2132d05d31c914a87c6611c10748aeb04b58e8f',
        chainId: 137,
        coinKey: 'USDT',
        decimals: 6,
        logoURI:
          'https://static.debank.com/image/eth_token/logo_url/0xdac17f958d2ee523a2206206994597c13d831ec7/66eadee7b7bb16b75e02b570ab8d5c01.png',
        name: 'USDT',

        symbol: 'USDT',
      },
      {
        address: '0x7ceb23fd6bc0add59e62ac25578270cff1b9f619',
        chainId: 137,
        coinKey: 'ETH',
        decimals: 18,
        logoURI: 'https://static.debank.com/image/token/logo_url/eth/935ae4e4d1d12d59a99717a24f2540b5.png',
        name: 'ETH',
        symbol: 'ETH',
      },
      {
        address: '0x0000000000000000000000000000000000000000',
        chainId: 137,
        coinKey: 'MATIC',
        decimals: 18,
        logoURI: 'https://static.debank.com/image/matic_token/logo_url/matic/e5a8a2860ba5cf740a474dcab796dc63.png',
        name: 'MATIC',

        symbol: 'MATIC',
      },
    ],
  },
  [SupportedChainId.POLYGON_MUMBAI]: {
    networkType: NetworkType.L1,
    blockWaitMsBeforeWarning: ms`10m`,
    docs: 'https://polygon.io/',
    explorer: 'https://mumbai.polygonscan.com/',
    infoLink: 'https://info.uniswap.org/#/polygon/',
    label: 'Polygon Mumbai',
    logoUrl: polygonLogo,
    headerLogoUrl: polygonLogoHeader,
    nativeCurrency: { name: 'Polygon Mumbai Matic', symbol: 'mMATIC', decimals: 18 },
  },

  [SupportedChainId.BSC_MAINNET]: {
    networkType: NetworkType.L1,
    explorer: 'https://www.bscscan.com',
    label: 'BSC',
    to: 43114,
    logoUrl: 'https://seeklogo.com/images/B/binance-smart-chain-bsc-logo-9C34053D61-seeklogo.com.png',
    headerLogoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/bsc.png',
    nativeCurrency: {
      address: '0x0000000000000000000000000000000000000000',
      chainId: 56,
      coinKey: 'BNB',
      decimals: 18,
      logoURI: 'https://static.debank.com/image/bsc_token/logo_url/bsc/8bfdeaa46fe9be8f5cd43a53b8d1eea1.png',
      name: 'BNB',
      symbol: 'BNB',
    },
    bridgeCurrency: [
      {
        address: '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d',
        chainId: 56,
        coinKey: 'USDC',
        decimals: 18,
        logoURI:
        'https://static.debank.com/image/eth_token/logo_url/0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48/fffcd27b9efff5a86ab942084c05924d.png',
        name: 'USDC',
        symbol: 'USDC',
      },
      {
        address: '0x55d398326f99059ff775485246999027b3197955',
        chainId: 56,
        coinKey: 'USDT',
        decimals: 18,
        logoURI:
          'https://static.debank.com/image/eth_token/logo_url/0xdac17f958d2ee523a2206206994597c13d831ec7/66eadee7b7bb16b75e02b570ab8d5c01.png',
        name: 'USDT',
        symbol: 'USDT',
      },
      {
        address: '0x2170ed0880ac9a755fd29b2688956bd959f933f8',
        chainId: 56,
        coinKey: 'ETH',
        decimals: 18,
        logoURI: 'https://static.debank.com/image/token/logo_url/eth/935ae4e4d1d12d59a99717a24f2540b5.png',
        name: 'ETH',
        symbol: 'ETH',
      },
      {
        address: '0x0000000000000000000000000000000000000000',
        chainId: 56,
        coinKey: 'BNB',
        decimals: 18,
        logoURI: 'https://static.debank.com/image/bsc_token/logo_url/bsc/8bfdeaa46fe9be8f5cd43a53b8d1eea1.png',
        name: 'BNB',
        symbol: 'BNB',
      },
    ],
  },
  [SupportedChainId.BSC_TESTNET]: {
    networkType: NetworkType.L1,
    explorer: 'https://testnet.bscscan.com',
    label: 'BSC',
    logoUrl: bscLogo,
    headerLogoUrl: bscLogoHeader,
    nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18, address: '0x0000000000000000000000000000000000000000' },
  },

  [SupportedChainId.AVALANCHE_MAINNET]: {
    networkType: NetworkType.L1,
    explorer: 'https://avascan.info',
    label: 'Avalanche',
    logoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/avalanche.png',
    headerLogoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/avalanche.png',
    to: 250,
    nativeCurrency: {
      address: '0x0000000000000000000000000000000000000000',
      chainId: 43114,
      coinKey: 'AVAX',
      decimals: 18,
      logoURI: 'https://static.debank.com/image/avax_token/logo_url/avax/0b9c84359c84d6bdd5bfda9c2d4c4a82.png',
      name: 'AVAX',
      symbol: 'AVAX',
    },
    bridgeCurrency: [
      {
        address: '0xa7d7079b0fead91f3e65f86e8915cb59c1a4c664',
        chainId: 43114,
        coinKey: 'USDC',
        decimals: 6,
        logoURI:
        'https://static.debank.com/image/eth_token/logo_url/0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48/fffcd27b9efff5a86ab942084c05924d.png',
        name: 'USDC',
        symbol: 'USDC',
      },
      {
        address: '0xc7198437980c041c805a1edcba50c1ce5db95118',
        chainId: 43114,
        coinKey: 'USDT',
        decimals: 6,
        logoURI:
          'https://static.debank.com/image/eth_token/logo_url/0xdac17f958d2ee523a2206206994597c13d831ec7/66eadee7b7bb16b75e02b570ab8d5c01.png',
        name: 'USDT',
        symbol: 'USDT',
      },
      {
        address: '0x49d5c2bdffac6ce2bfdb6640f4f80f226bc10bab',
        chainId: 43114,
        coinKey: 'ETH',
        decimals: 18,
        logoURI:
          'https://static.debank.com/image/token/logo_url/eth/935ae4e4d1d12d59a99717a24f2540b5.png',
        name: 'ETH',
        priceUSD: '1953.98',
        symbol: 'ETH',
      },
      {
        address: '0x0000000000000000000000000000000000000000',
        chainId: 43114,
        coinKey: 'AVAX',
        decimals: 18,
        logoURI: 'https://static.debank.com/image/avax_token/logo_url/avax/0b9c84359c84d6bdd5bfda9c2d4c4a82.png',
        name: 'AVAX',
        symbol: 'AVAX',
      },
    ],
  },
  [SupportedChainId.AVALANCHE_FUJI_TESTNET]: {
    networkType: NetworkType.L1,
    explorer: 'https://testnet.avascan.info/',
    label: 'Avalanche',
    logoUrl: avalancheLogo,
    headerLogoUrl: avalancheLogoHeader,
    nativeCurrency: { name: 'AVAX', symbol: 'AVAX', decimals: 18 },
  },

  [SupportedChainId.FANTOM_OPERA_MAINNET]: {
    networkType: NetworkType.L1,
    explorer: 'https://ftmscan.com',
    label: 'Fantom',
    logoUrl: 'https://cryptologos.cc/logos/fantom-ftm-logo.png',
    headerLogoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/fantom.png',
    to: 42161,
    nativeCurrency: {
      address: '0x0000000000000000000000000000000000000000',
      chainId: 250,
      coinKey: 'FTM',
      decimals: 18,
      logoURI: 'https://static.debank.com/image/ftm_token/logo_url/ftm/33fdb9c5067e94f3a1b9e78f6fa86984.png',
      name: 'FTM',
      symbol: 'FTM',
    },
    bridgeCurrency: [
      {
        address: '0x04068da6c83afcfa0e13ba15a6696662335d5b75',
        chainId: 250,
        coinKey: 'USDC',
        decimals: 6,
        logoURI:
        'https://static.debank.com/image/eth_token/logo_url/0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48/fffcd27b9efff5a86ab942084c05924d.png',
        name: 'USDC',
        symbol: 'USDC',
      },
      {
        address: '0x049d68029688eabf473097a2fc38ef61633a3c7a',
        chainId: 250,
        coinKey: 'USDT',
        decimals: 6,
        logoURI:
          'https://static.debank.com/image/eth_token/logo_url/0xdac17f958d2ee523a2206206994597c13d831ec7/66eadee7b7bb16b75e02b570ab8d5c01.png',
        name: 'USDT',
        symbol: 'USDT',
      },
      {
        address: '0x74b23882a30290451a17c44f4f05243b6b58c76d',
        chainId: 250,
        coinKey: 'ETH',
        decimals: 18,
        logoURI: 'https://static.debank.com/image/token/logo_url/eth/935ae4e4d1d12d59a99717a24f2540b5.png',
        name: 'ETH',
        symbol: 'ETH',
      },
      {
        address: '0x0000000000000000000000000000000000000000',
        chainId: 250,
        coinKey: 'FTM',
        decimals: 18,
        logoURI: 'https://static.debank.com/image/ftm_token/logo_url/ftm/33fdb9c5067e94f3a1b9e78f6fa86984.png',
        name: 'FTM',
        symbol: 'FTM',
      },
    ],
  },
  [SupportedChainId.FANTOM_OPERA_TESTNET]: {
    networkType: NetworkType.L1,
    explorer: 'https://ftmscan.com',
    label: 'Fantom',
    logoUrl: fantomLogo,
    headerLogoUrl: fantomLogoHeader,
    nativeCurrency: { name: 'Fantom', symbol: 'FTM', decimals: 18 },
  },
  [SupportedChainId.ARBITRUM_ONE]: {
    networkType: NetworkType.L2,
    explorer: 'https://arbiscan.io/',
    bridge: '/app#/bridge',
    label: 'Arbitrum',
    logoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/arbitrum.png',
    to: 42220,
    headerLogoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/arbitrum.png',
    nativeCurrency: {
      address: '0x0000000000000000000000000000000000000000',
      chainId: 42161,
      coinKey: 'ETH',
      decimals: 18,
      logoURI: 'https://static.debank.com/image/arb_token/logo_url/arb/d61441782d4a08a7479d54aea211679e.png',
      name: 'ETH',
      symbol: 'ETH',
    },
    bridgeCurrency: [
      {
        address: '0xff970a61a04b1ca14834a43f5de4533ebddb5cc8',
        chainId: 42161,
        coinKey: 'USDC',
        decimals: 6,
        logoURI:
        'https://static.debank.com/image/eth_token/logo_url/0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48/fffcd27b9efff5a86ab942084c05924d.png',
        name: 'USDC',
        symbol: 'USDC',
      },
      {
        address: '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9',
        chainId: 42161,
        coinKey: 'USDT',
        decimals: 6,
        logoURI:
          'https://static.debank.com/image/eth_token/logo_url/0xdac17f958d2ee523a2206206994597c13d831ec7/66eadee7b7bb16b75e02b570ab8d5c01.png',
        name: 'Tether USD',
        symbol: 'USDT',
      },
      {
        address: '0x0000000000000000000000000000000000000000',
        chainId: 42161,
        coinKey: 'ETH',
        decimals: 18,
        logoURI: 'https://static.debank.com/image/token/logo_url/eth/935ae4e4d1d12d59a99717a24f2540b5.png',
        name: 'ETH',
        symbol: 'ETH',
      },
    ],
  },
  [SupportedChainId.CELO]: {
    networkType: NetworkType.L1,
    explorer: 'https://explorer.celo.org/',
    bridge: '/app#/bridge',
    label: 'Celo',
    logoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/celo.png',
    to: 122,
    headerLogoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/celo.png',
    nativeCurrency: {
      address: '0x471ece3750da237f93b8e339c536989b8978a438',
      chainId: 42220,
      coinKey: 'CELO',
      decimals: 18,
      logoURI:
        'https://static.debank.com/image/celo_token/logo_url/0x471ece3750da237f93b8e339c536989b8978a438/6f524d91db674876ba0f5767cf0124cc.png',
      name: 'CELO',
      symbol: 'CELO',
    },
    bridgeCurrency: [
      {
        address: '0xef4229c8c3250c675f21bcefa42f58efbff6002a',
        chainId: 42220,
        coinKey: 'USDC',
        decimals: 6,
        logoURI:
        'https://static.debank.com/image/eth_token/logo_url/0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48/fffcd27b9efff5a86ab942084c05924d.png',
        name: 'USDC',
        symbol: 'USDC',
      },
      {
        address: '0x88eec49252c8cbc039dcdb394c0c2ba2f1637ea0',
        chainId: 42220,
        coinKey: 'USDT',
        decimals: 6,
        logoURI:
          'https://static.debank.com/image/eth_token/logo_url/0xdac17f958d2ee523a2206206994597c13d831ec7/66eadee7b7bb16b75e02b570ab8d5c01.png',
        name: 'USDT',
        symbol: 'USDT',
      },
      {
        address: '0x122013fd7df1c6f636a5bb8f03108e876548b455',
        chainId: 42220,
        coinKey: 'ETH',
        decimals: 18,
        logoURI:
          'https://static.debank.com/image/token/logo_url/eth/935ae4e4d1d12d59a99717a24f2540b5.png',
        name: 'ETH',
        symbol: 'ETH',
      },
      {
        address: '0x471ece3750da237f93b8e339c536989b8978a438',
        chainId: 42220,
        coinKey: 'CELO',
        decimals: 18,
        logoURI:
          'https://static.debank.com/image/celo_token/logo_url/0x471ece3750da237f93b8e339c536989b8978a438/6f524d91db674876ba0f5767cf0124cc.png',
        name: 'CELO',
        symbol: 'CELO',
      },
    ],
  },
  
  [SupportedChainId.GNOSIS]: {
    networkType: NetworkType.L2,
    explorer: 'https://blockscout.com/xdai/mainnet/',
    bridge: '/app#/bridge',
    label: 'Gnosis',
    logoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/gnosis.png',
    to: 1284,
    headerLogoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/gnosis.png',
    nativeCurrency: {
      name: 'XDAI ',
      symbol: 'XDAI',
      logoURI:
      'https://static.debank.com/image/xdai_token/logo_url/xdai/1207e67652b691ef3bfe04f89f4b5362.png',
      decimals: 18,
      address: '0x0000000000000000000000000000000000000000',
    },
    bridgeCurrency: [
      {
        address: '0xddafbb505ad214d7b80b1f830fccc89b60fb7a83',
        chainId: 100,
        coinKey: 'USDC',
        decimals: 6,
        logoURI:
        'https://static.debank.com/image/eth_token/logo_url/0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48/fffcd27b9efff5a86ab942084c05924d.png',
        name: 'USDC',
        symbol: 'USDC',
      },
      {
        address: '0x4ecaba5870353805a9f068101a40e0f32ed605c6',
        chainId: 100,
        coinKey: 'USDT',
        decimals: 6,
        logoURI:
          'https://static.debank.com/image/eth_token/logo_url/0xdac17f958d2ee523a2206206994597c13d831ec7/66eadee7b7bb16b75e02b570ab8d5c01.png',
        name: 'USDT',
        symbol: 'USDT',
      },
      {
        address: '0x6a023ccd1ff6f2045c3309768ead9e68f978f6e1',
        chainId: 100,
        coinKey: 'ETH',
        decimals: 18,
        logoURI:
          'https://static.debank.com/image/token/logo_url/eth/935ae4e4d1d12d59a99717a24f2540b5.png',
        name: 'ETH',
        priceUSD: '1958.23',
        symbol: 'ETH',
      },
      {
        address: '0x0000000000000000000000000000000000000000',
        chainId: 100,
        coinKey: 'XDAI',
        decimals: 18,
        logoURI:
          'https://static.debank.com/image/xdai_token/logo_url/xdai/1207e67652b691ef3bfe04f89f4b5362.png',
        name: 'XDAI',
        priceUSD: '1958.23',
        symbol: 'XDAI',
      },
    ],
  },
  
  [SupportedChainId.CRONOS]: {
    networkType: NetworkType.L1,
    explorer: 'https://cronos.org/explorer',
    bridge: '/app#/bridge',
    label: 'Cronos',
    logoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/cronos.png',
    to: 1285,
    headerLogoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/cronos.png',
    nativeCurrency: {
      address: '0x0000000000000000000000000000000000000000',
      chainId: 25,
      coinKey: 'CRO',
      decimals: 18,
      logoURI: 'https://static.debank.com/image/cro_token/logo_url/cro/affddd53019ffb9dbad0c724e12500c0.png',
      name: 'CRO',
      symbol: 'CRO',
    },
    bridgeCurrency: [
      {
        address: '0xc21223249ca28397b4b6541dffaecc539bff0c59',
        chainId: 25,
        coinKey: 'USDC',
        decimals: 6,
        logoURI:
        'https://static.debank.com/image/eth_token/logo_url/0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48/fffcd27b9efff5a86ab942084c05924d.png',
        name: 'USDC',
        symbol: 'USDC',
      },
      {
        address: '0x66e428c3f67a68878562e79a0234c1f83c208770',
        chainId: 25,
        coinKey: 'USDT',
        decimals: 6,
        logoURI:
          'https://static.debank.com/image/eth_token/logo_url/0xdac17f958d2ee523a2206206994597c13d831ec7/66eadee7b7bb16b75e02b570ab8d5c01.png',
        name: 'USDT',
        symbol: 'USDT',
      },
      {
        address: '0xe44Fd7fCb2b1581822D0c862B68222998a0c299a',
        chainId: 25,
        coinKey: 'ETH',
        decimals: 18,
        logoURI:
          'https://static.debank.com/image/token/logo_url/eth/935ae4e4d1d12d59a99717a24f2540b5.png',
        name: 'ETH',
        symbol: 'ETH',
      },
      {
        address: '0x0000000000000000000000000000000000000000',
        chainId: 25,
        coinKey: 'CRO',
        decimals: 18,
        logoURI: 'https://static.debank.com/image/cro_token/logo_url/cro/affddd53019ffb9dbad0c724e12500c0.png',
        name: 'CRO',
        symbol: 'CRO',
      },
    ],
  },
 
  [SupportedChainId.OPTIMISTIC_ETHEREUM]: {
    networkType: NetworkType.L2,
    explorer: 'https://optimistic.etherscan.io/',
    bridge: '/app#/bridge',
    label: 'Optimism',
    logoUrl: 'data:image/png;base64,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',
    to: 1,
    headerLogoUrl: 'https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/optimism.png',
    nativeCurrency: {
      address: '0x0000000000000000000000000000000000000000',
      chainId: 10,
      coinKey: 'ETH',
      decimals: 18,
      logoURI: 'https://static.debank.com/image/op_token/logo_url/op/d61441782d4a08a7479d54aea211679e.png',
      name: 'ETH',
      symbol: 'ETH',
    },
    bridgeCurrency: [
      {
        address: '0x7f5c764cbc14f9669b88837ca1490cca17c31607',
        chainId: 10,
        coinKey: 'USDC',
        decimals: 6,
        logoURI:
        'https://static.debank.com/image/eth_token/logo_url/0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48/fffcd27b9efff5a86ab942084c05924d.png',
        name: 'USDC',
        symbol: 'USDC',
      },
      {
        address: '0x94b008aa00579c1307b0ef2c499ad98a8ce58e58',
        chainId: 10,
        coinKey: 'USDT',
        decimals: 6,
        logoURI:
          'https://static.debank.com/image/eth_token/logo_url/0xdac17f958d2ee523a2206206994597c13d831ec7/66eadee7b7bb16b75e02b570ab8d5c01.png',
        name: 'USDT',
        symbol: 'USDT',
      },
      {
        address: '0x0000000000000000000000000000000000000000',
        chainId: 10,
        coinKey: 'ETH',
        decimals: 18,
        logoURI: 'https://static.debank.com/image/token/logo_url/eth/935ae4e4d1d12d59a99717a24f2540b5.png',
        name: 'ETH',
        symbol: 'ETH',
      },
    ],
  },
}
